import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Shield, Cpu } from 'lucide-react';

/**
 * DepartmentSelectorModal — full-screen-ish dialog with two large cards (Admin / IT).
 * After selection, navigates to /tickets/create?module={Admin|IT}.
 */
export default function DepartmentSelectorModal({ open, onOpenChange }) {
  const navigate = useNavigate();

  const pick = (module) => {
    onOpenChange(false);
    navigate(`/tickets/create?module=${module}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Raise a new request</DialogTitle>
          <DialogDescription>Choose the department for your request</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <button
            type="button"
            onClick={() => pick('Admin')}
            className="group relative overflow-hidden border-2 border-slate-200 hover:border-blue-500 hover:shadow-lg rounded-xl p-6 transition-all bg-white dark:bg-slate-900 dark:border-slate-700 dark:hover:border-blue-400 text-left"
            data-testid="dept-card-admin"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Shield className="w-7 h-7 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">Admin</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Stationery, Meeting Room, Insurance, Travel, Courier, Bouquet, Snacks, and other admin requests
                </p>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          <button
            type="button"
            onClick={() => pick('IT')}
            className="group relative overflow-hidden border-2 border-slate-200 hover:border-emerald-500 hover:shadow-lg rounded-xl p-6 transition-all bg-white dark:bg-slate-900 dark:border-slate-700 dark:hover:border-emerald-400 text-left"
            data-testid="dept-card-it"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Cpu className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">IT</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Hardware, Software, Network, Email, Application access, and other IT support tickets
                </p>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
